import React, { useMemo } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Container, Title, Card, Text, LoadingOverlay, Alert } from '@mantine/core';
import useAuthenticatedData from '../../../hooks/useAuthenticatedData';
import config from '../../../config';

const SalesHistoryChart = ({ ccode }) => {
  // Construct the API URL with ccode
  const url = `${config.apiBaseUrl}/sales/scorecard/${ccode}/sales-history`;
  
  // Use your custom hook to fetch data
  const { data, isLoading, error } = useAuthenticatedData(url);

  // Transform the API data into the format expected by the chart
  const chartData = useMemo(() => {
    if (!data?.result) return [];
    
    return data.result
      .map(item => ({
        month: item.values.monthlabel,
        sales: item.values.bookedsales,
        monthEndDate: item.values.monthenddate
      }))
      .sort((a, b) => new Date(a.monthEndDate) - new Date(b.monthEndDate)); // Sort by date ascending
  }, [data]);

  // Get salesperson name from the first record
  const salespersonName = useMemo(() => {
    if (!data?.result || data.result.length === 0) return '';
    return data.result[0].values.fullname;
  }, [data]);

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div style={{
          backgroundColor: 'white',
          padding: '10px',
          border: '1px solid #ccc',
          borderRadius: '4px',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
        }}>
          <p style={{ margin: 0, fontWeight: 'bold' }}>{label}</p>
          <p style={{ margin: 0, color: '#2563eb' }}>
            Sales: {formatCurrency(payload[0].value)}
          </p>
        </div>
      );
    }
    return null;
  };

  if (error) {
    return (
      <Container size="lg" py="xl">
        <Alert color="red" title="Error">
          {error.message}
        </Alert>
      </Container>
    );
  }

  return (
    <Container size="lg" py="xl">
      <Title order={2} mb="md">
        {salespersonName ? `${salespersonName}'s Sales Performance` : 'Sales Performance'}
      </Title>
      
      <Card shadow="sm" padding="lg" radius="md" withBorder>
        <LoadingOverlay visible={isLoading} overlayBlur={2} />
        
        <Text size="sm" color="dimmed" mb="md">
          Monthly sales performance for the past 12 months
        </Text>
        
        <ResponsiveContainer width="100%" height={400}>
          <LineChart
            data={chartData}
            margin={{
              top: 5,
              right: 30,
              left: 20,
              bottom: 5,
            }}
          >
            <CartesianGrid strokeDasharray="3 3" />
            <XAxis 
              dataKey="month" 
              tick={{ fontSize: 12 }}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              tick={{ fontSize: 12 }}
              tickFormatter={formatCurrency}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend />
            <Line
              type="line"
              dataKey="sales"
              stroke="#2563eb"
              strokeWidth={2}
              dot={{ fill: '#2563eb', strokeWidth: 2, r: 4 }}
              activeDot={{ r: 6 }}
              name="Booked Sales"
            />
          </LineChart>
        </ResponsiveContainer>
        
        {chartData.length > 0 && (
          <Text size="xs" color="dimmed" mt="sm">
            Total Sales: {formatCurrency(chartData.reduce((sum, item) => sum + item.sales, 0))}
          </Text>
        )}
      </Card>
    </Container>
  );
};

export default SalesHistoryChart;